import {
  ApiProduct, ApiProductSize, ApiProductStockDetail, ApiProductStock, ApiProductsList, ApiProductPromotions
} from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import { ApiEmpathySearch } from 'idt-ecommerce-api/_api/providers/api-empathy/api-empathy.models';
import { CategoryRelated } from 'idt-ecommerce-api/catalog/categories/categories.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { MoneyHelpers } from 'idt-ecommerce-api/util/money.helpers';
import { UrlsWebHelpers } from 'idt-ecommerce-api/util/urls-web.helpers';
// eslint-disable-next-line no-restricted-imports
import { GrowthbookConfig } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';

import { ProductFeaturesModelsTransformer } from './features/product-features.models.transformer';
import { ProductMetadataModelsTransformer } from './metadata/product-metadata.models.transformer';
import {
  Product,
  ProductColor,
  ProductSize,
  ProductGridElementType,
  ProductPrice,
  ProductTechnicalDetailModel,
  ProductCompositionModel,
  ProductCareModel,
  SearchProduct,
  ProductPartNumberDetails,
  ProductPromotion
} from './products.models';
import { SizesModelsTransformer } from './sizes/sizes.models.transformer';

/**
 * Clase de utilidad para transformar los objetos que devuelve el api de inditex en los objetos que usaremos en el cms,
 * widgets y render
 */
export class ProductsModelsTransformer {

  public static productsFromApi(
    productList: ApiProductsList,
    context: EcommerceContext,
    categoryId?: string,
    stocks?: ApiProductStock[],
    growthbookConfig?: GrowthbookConfig[]
  ): Product[] {
    const apiProducts = productList.products;
    const products = apiProducts.filter(
      (apiProduct) => !(apiProduct.type === 'ProductBean' && apiProduct.detail?.colors?.length === 0)
        && !(apiProduct.type === 'BundleBean' && apiProduct.bundleProductSummaries.length === 0)
    ).map(
      (apiProduct: ApiProduct) => this.productFromApi(
        apiProduct, context, categoryId, stocks, false, undefined, growthbookConfig
      )
    );

    return products;
  }

  public static productsFromApiEmpathy(searchResponse: ApiEmpathySearch): SearchProduct[] {
    const products: SearchProduct[] = [];
    const empathyProducts = searchResponse.catalog.content;

    for (const empathyProduct of empathyProducts) {
      const product = { id: empathyProduct.productId, name: empathyProduct.name };
      products.push(product);
    }
    return products;
  }

  public static productFromApi(
    apiProduct: ApiProduct,
    context: EcommerceContext,
    categoryId?: string,
    stocks: ApiProductStock[] = [],
    fromDetail: boolean = false,
    relatedMocacos: ApiProduct[] | undefined = undefined,
    growthbookConfig: GrowthbookConfig[] = []
  ): Product {
    const gridElemType = apiProduct.gridElemType as ProductGridElementType || ProductGridElementType.PRODUCT;

    const productPartNumberDetails: ProductPartNumberDetails | undefined = this
      .getDetailsFromPartNumber(apiProduct.detail.reference, apiProduct.mainColorid);

    apiProduct = this.convertApiProductIfNeccesary(apiProduct, gridElemType);

    // transform apiProduct info with mocacos if is defined
    if (context.config.brandSettings.transformProductWithMocacos && relatedMocacos) {
      apiProduct = context.config.brandSettings.transformProductWithMocacos(apiProduct, relatedMocacos);
    }

    const id = apiProduct.id.toString();
    const name = apiProduct.name;
    const nameEn = apiProduct.nameEn;
    const longDescription = apiProduct.detail.longDescription || undefined;
    const type = apiProduct.type;
    const idProductToSearchStock = this.getProductIdForStock(apiProduct);
    const productStock = stocks.find((stock: ApiProductStock) => stock.productId === idProductToSearchStock);

    const colors = (apiProduct.detail.colors ?? []).map(
      (apiColor) => ProductFeaturesModelsTransformer.productColorObjectFromApi(
        apiColor,
        apiProduct,
        context,
        (productStock?.stocks ?? []),
        productPartNumberDetails?.style,
        growthbookConfig
      )
    );

    const bundleProducts = (apiProduct?.bundleProductSummaries ?? []).map(
      (it) => this.productFromApi(it, context, categoryId, stocks)
    );

    const urlWeb = UrlsWebHelpers.getProductUrl(context, id, categoryId);
    const metadata = ProductMetadataModelsTransformer.parseProductMetadata(apiProduct, fromDetail, context);
    const productReference = apiProduct.detail.displayReference;
    const fullReference = apiProduct.detail.reference;
    const productType = apiProduct.productType;
    const urlCMS = UrlsWebHelpers.getProductUrlCMS(context, id, apiProduct.productUrl, categoryId);
    const composition: ProductCompositionModel[] = ProductFeaturesModelsTransformer.productComposition(apiProduct);
    const care: ProductCareModel[] = ProductFeaturesModelsTransformer.productCare(apiProduct, context);
    const technicalDetails: ProductTechnicalDetailModel[] = ProductFeaturesModelsTransformer.technicalDetails(
      apiProduct, context
    );
    const relatedProducts: Product[] = this.relatedProductsFromApi(apiProduct, context, categoryId, stocks);
    const relatedCategories: CategoryRelated[] = this.relatedCategoriesFromApi(apiProduct);
    const familyName = apiProduct.detail.familyInfo?.familyName || undefined;
    const familyCode = apiProduct.family ?? apiProduct.detail.familyInfo?.familyCode?.toString();
    const section = apiProduct.section;
    const subFamilyName = apiProduct.detail.subfamilyInfo?.subFamilyName || undefined;
    const subFamilyCode = apiProduct.subFamily ?? apiProduct.detail.subfamilyInfo?.subFamilyCode?.toString();

    let priceInfo = this.pricesFromApi(colors);
    if (bundleProducts.length > 0) {
      priceInfo = ProductFeaturesModelsTransformer.pricesFromMultipleProducts(bundleProducts, context);
      metadata.labels = ProductFeaturesModelsTransformer.labelsFromMultipleProducts(bundleProducts);
    }

    const alternateLocaleCurrencyInfo = context.store.details.alternateLocaleInfo;
    if (alternateLocaleCurrencyInfo) {
      if (priceInfo.price) {
        priceInfo.localePrice = ProductFeaturesModelsTransformer.alternateLocalePriceApplyingConversion(
          alternateLocaleCurrencyInfo.conversionRate,
          priceInfo.price
        );
        priceInfo.localePriceWithCurrencySymbol = MoneyHelpers.formatPriceWithCurrencySymbol(
          priceInfo.localePrice,
          alternateLocaleCurrencyInfo
        );
        if (priceInfo.prewarming?.price) {
          priceInfo.prewarming.localePriceWithCurrency = MoneyHelpers.formatPriceWithCurrencySymbol(
            ProductFeaturesModelsTransformer.alternateLocalePriceApplyingConversion(
              alternateLocaleCurrencyInfo.conversionRate,
              Number.parseInt(priceInfo.prewarming.price, 10)
            ),
            alternateLocaleCurrencyInfo
          );
        }
      }
      if (priceInfo.oldPrice) {
        priceInfo.oldLocalePrice = ProductFeaturesModelsTransformer.alternateLocalePriceApplyingConversion(
          alternateLocaleCurrencyInfo.conversionRate,
          priceInfo.oldPrice
        );
        priceInfo.oldLocalePriceWithCurrencySymbol = MoneyHelpers.formatPriceWithCurrencySymbol(
          priceInfo.oldLocalePrice,
          alternateLocaleCurrencyInfo
        );
      }
    }

    const localCurrencyInfo = context.store.details.currencyInfo;
    if (priceInfo.originalPrice && localCurrencyInfo) {
      priceInfo.originalPriceWithCurrencySymbol = MoneyHelpers.formatPriceWithCurrencySymbol(
        priceInfo.originalPrice,
        localCurrencyInfo
      );
    }

    if (!!priceInfo.prewarming?.price && !!priceInfo.price) {
      priceInfo.prewarming.discountRate = MoneyHelpers.getTruncateDiscountFromPrice(
        Number.parseInt(priceInfo.prewarming.price, 10),
        priceInfo.price
      );
    }

    if (!!priceInfo.prewarming?.promotionId && !!priceInfo.price) {
      priceInfo.prewarming.description = ProductsModelsTransformer.getPromotionDescription(
        priceInfo.prewarming.promotionId,
        apiProduct.detail.promotions
      );
    }

    if (!!priceInfo.promotion?.promotionId && !!priceInfo.price) {
      priceInfo.promotion = ProductsModelsTransformer.getPromotion(
        priceInfo.promotion?.promotionId,
        apiProduct.detail.promotions
      );
    }

    const product: Product = {
      id,
      name,
      nameEn,
      longDescription,
      priceInfo,
      colors,
      urlWeb,
      productUrl: apiProduct.productUrl ?? '',
      productUrlParam: apiProduct.productUrlParam,
      type,
      gridElemType,
      bundleProducts,
      metadata,
      urlCMS,
      productReference,
      fullReference,
      technicalDetails,
      composition,
      care,
      relatedProducts,
      productType,
      relatedCategories,
      familyName,
      familyCode,
      section,
      subFamilyName,
      subFamilyCode,
      ...(apiProduct.detail.additionalInfo) && { additionalInfo: apiProduct.detail.additionalInfo },
      ...(apiProduct.detail.fullReferenceBundle) && { fullReferenceBundle: apiProduct.detail.fullReferenceBundle }
    };

    if (isDefined(relatedMocacos)) {
      product.relatedMocacos = relatedMocacos.map((mocaco) => this.productFromApi(mocaco, context, categoryId));
    }

    if (productPartNumberDetails) {
      product.productPartNumberDetails = productPartNumberDetails;
    }

    return product;
  }

  public static pricesFromApi(colors: ProductColor[]): ProductPrice {
    let productPrice: ProductPrice = { };
    if ((colors.length > 0) && (colors[0]?.sizes ?? []).length > 0) {
      productPrice = colors[0].sizes![0].priceInfo ?? {};
    }
    return productPrice;
  }

  public static productSizeObjectFromApi(
    apiProductSize: ApiProductSize[],
    productStock: ApiProductStockDetail[],
    context: EcommerceContext
  ): ProductSize[] {
    return SizesModelsTransformer.productSizeObjectFromApi(apiProductSize, productStock, context);
  }

  // ---- Private functions ----
  private static getProductIdForStock(apiProduct: ApiProduct) {
    return apiProduct.mocacoOrEstilismoSourceProductId ? apiProduct.mocacoOrEstilismoSourceProductId : apiProduct.id;
  }

  private static convertApiProductIfNeccesary(
    apiProduct: ApiProduct,
    gridElemType: ProductGridElementType
  ): ApiProduct {
    if ([ProductGridElementType.MOCACO, ProductGridElementType.ESTILISMO].includes(gridElemType)) {
      apiProduct = ProductFeaturesModelsTransformer.setBundleInfoToProduct(apiProduct, gridElemType);
    } else if (gridElemType === ProductGridElementType.BUNDLE) {
      if (isDefined(apiProduct.bundleProductSummaries) && apiProduct.bundleProductSummaries.length > 0) {
        apiProduct.detail.colors = apiProduct.bundleProductSummaries[0].detail.colors;
      }
    } else if (apiProduct.type === 'BundleBean' && gridElemType === ProductGridElementType.PRODUCT) {
      apiProduct = ProductFeaturesModelsTransformer.setBundleInfoToProduct(apiProduct, gridElemType);
    }

    return apiProduct;
  }

  private static relatedProductsFromApi(
    apiProduct: ApiProduct,
    context: EcommerceContext,
    categoryId?: string,
    stocks?: ApiProductStock[]
  ) {
    let relatedProduct: Product[] = [];
    if (apiProduct.detail.relatedProducts) {
      relatedProduct = apiProduct.detail.relatedProducts.map(
        (relatedProductApi: ApiProduct) => this.productFromApi(relatedProductApi, context, categoryId, stocks)
      );
    }
    return relatedProduct;
  }

  private static relatedCategoriesFromApi(apiProduct: ApiProduct) {
    let relatedCategories: CategoryRelated[] = [];
    if (apiProduct.relatedCategories) {
      relatedCategories = apiProduct.relatedCategories?.map((related) => {
        return { name: related.name };
      });
    }
    return relatedCategories;
  }

  private static getPromotionDescription(
    promotionId: number,
    apiPromotions?: ApiProductPromotions[]
  ): string | undefined {
    if (!apiPromotions) {
      return undefined;
    }
    const promotion = apiPromotions.find((apiPromotion) => apiPromotion.promotionId === promotionId);
    return promotion?.description;
  }

  private static getPromotion(
    promotionId: number,
    apiPromotions?: ApiProductPromotions[]
  ): ProductPromotion | undefined {
    if (!apiPromotions) {
      return undefined;
    }
    const promotion = apiPromotions.find((apiPromotion) => apiPromotion.promotionId === promotionId);
    return {
      promotionId,
      description: promotion?.description,
      startDate: promotion?.startDate,
      endDate: promotion?.endDate
    };
  }

  /**
   * Devuelve los detalles necesarios para enlazar un producto con su color y/o color y estilo.
   *
   * @param partNumber Usado para obtener el color y/o color y estilo.
   * @param mainColor
   * @param colors
   * @returns Si el tipo de producto es un MOCACO: color. Si es un ESTILISMO: color y estilo. En
   * caso de ser otro tipo se devuelve el color y estilo principal de ese producto
   *
   * Ref: https://axinic.central.inditex.grp/confluence/pages/viewpage.action?pageId=444812995
   */
  private static getDetailsFromPartNumber(
    partNumber: string,
    mainColor: string | undefined
  ): ProductPartNumberDetails | undefined {
    const estilismoPattern = /C[0-9]{11}[0-9]{2}-[V,I][0-9]{4}/g;
    const mocacoPattern = /C[0-9]{11}-[V,I][0-9]{4}/g;

    if (mocacoPattern.test(partNumber)) {
      return {
        colorId: partNumber.split('-')[0].slice(-3)
      };
    }

    if (estilismoPattern.test(partNumber)) {
      return {
        colorId: partNumber.split('-')[0].substring(9, 12),
        style: partNumber.split('-')[0].slice(-2)
      };
    }

    return mainColor ? { colorId: mainColor } : undefined;
  }

}
